import React, { useState, useContext, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { FaBars, FaTimes, FaMoon, FaSun } from "react-icons/fa";
import { ThemeContext } from "../index";

const NAV_LINKS = [
    { label: "Home", path: "/" },
    { label: "Projects", path: "/projects" },
    { label: "Experience", path: "/experience" },
    { label: "Blog", path: "/blog" },
    { label: "Contact", path: "/contact" },
];

function NavBar() {
    const [menuOpen, setMenuOpen] = useState(false);
    const { theme, toggle } = useContext(ThemeContext);

    useEffect(() => {
        if (menuOpen) document.body.style.overflow = "hidden";
        else document.body.style.overflow = "";
        return () => { document.body.style.overflow = ""; };
    }, [menuOpen]);

    const getLinkClass = ({ isActive }) =>
        `relative text-[0.8125rem] font-medium tracking-wide px-3 py-2 rounded-[4px] transition-colors duration-200 focus-ring ${
            isActive
                ? "text-primary"
                : "text-on-surface-variant hover:text-on-surface"
        }`;

    return (
        <>
            {/* Top bar */}
            <nav
                aria-label="Main navigation"
                className="glass-nav fixed top-0 inset-x-0 z-50 h-16 md:h-[4.5rem]"
            >
                <div className="max-w-7xl mx-auto px-6 h-full flex items-center justify-between">
                    {/* Logo */}
                    <NavLink to="/" aria-label="Raviraj — Home" className="focus-ring rounded-[4px]">
                        <span className="font-signature text-[2rem] text-primary leading-none select-none">
                            Raviraj
                        </span>
                    </NavLink>

                    {/* Desktop links */}
                    <ul className="hidden md:flex items-center gap-1" role="menubar">
                        {NAV_LINKS.map(({ label, path }) => (
                            <li key={path} role="none" className="relative">
                                <NavLink
                                    to={path}
                                    end={path === "/"}
                                    className={getLinkClass}
                                    role="menuitem"
                                >
                                    {({ isActive }) => (
                                        <>
                                            {label}
                                            {isActive && (
                                                <span
                                                    aria-hidden="true"
                                                    className="absolute -bottom-1 left-1/2 -translate-x-1/2 h-[2px] w-4 rounded-full bg-primary"
                                                />
                                            )}
                                        </>
                                    )}
                                </NavLink>
                            </li>
                        ))}
                        <li role="none">
                            <button
                                onClick={toggle}
                                aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
                                className="ml-3 p-2 rounded-[4px] text-on-surface-variant hover:text-on-surface hover:bg-surface-container focus-ring transition-colors"
                            >
                                {theme === "dark" ? <FaSun size={15} /> : <FaMoon size={15} />}
                            </button>
                        </li>
                    </ul>

                    {/* Mobile hamburger */}
                    <button
                        className="md:hidden p-2 text-on-surface-variant focus-ring rounded-[4px] relative z-[60]"
                        onClick={() => setMenuOpen(!menuOpen)}
                        aria-label={menuOpen ? "Close navigation" : "Open navigation"}
                        aria-expanded={menuOpen}
                    >
                        {menuOpen ? <FaTimes size={18} /> : <FaBars size={18} />}
                    </button>
                </div>
            </nav>

            {/* Mobile menu — rendered outside <nav> to avoid clipping/stacking issues */}
            {menuOpen && (
                <div
                    className="md:hidden fixed inset-0 top-16 z-[55] flex flex-col overflow-y-auto"
                    style={{ background: "var(--clr-surface)" }}
                >
                    <div className="flex flex-col px-6 py-4 gap-1">
                        {NAV_LINKS.map(({ label, path }) => (
                            <NavLink
                                key={path}
                                to={path}
                                end={path === "/"}
                                className={({ isActive }) =>
                                    `py-3.5 px-4 text-base font-medium rounded-[4px] transition-colors ${
                                        isActive
                                            ? "text-primary bg-surface-low"
                                            : "text-on-surface-variant hover:text-on-surface hover:bg-surface-container"
                                    }`
                                }
                                onClick={() => setMenuOpen(false)}
                            >
                                {label}
                            </NavLink>
                        ))}
                        <button
                            onClick={() => { toggle(); setMenuOpen(false); }}
                            className="mt-4 py-3 px-4 text-sm font-medium text-on-surface-variant hover:text-on-surface rounded-[4px] self-start"
                            style={{ border: "1px solid rgba(255,255,255,0.08)" }}
                        >
                            {theme === "dark" ? "☀  Light Mode" : "🌙  Dark Mode"}
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}

export default NavBar;
